import { Accordion, Form, Button, Badge } from 'react-bootstrap';
import { FaTools, FaPlus, FaCode, FaExternalLinkAlt, FaEdit, FaTrash, FaSearch, FaEnvelope, FaPhone } from 'react-icons/fa';

export interface APIToolConfig {
    id: string;
    name: string;
    description: string;
    method: string;
    url: string;
    headers?: Record<string, ParameterConfig>;
    params?: Record<string, ParameterConfig>;
    data?: Record<string, ParameterConfig> | any;
    responseType?: string;
    enabled?: boolean;
}

interface ParameterConfig {
    value: string;
    required: boolean;
    type: 'user-input' | 'system-value';
    description?: string;
}

export interface PredefinedTool {
    id: string;
    name: string;
    description: string;
    enabled: boolean;
}

interface ToolsConfigurationProps {
    predefinedTools: PredefinedTool[];
    apiTools: APIToolConfig[];
    permission: boolean;
    onTogglePredefinedTool: (toolId: string) => void;
    onToggleApiTool: (toolId: string) => void;
    onAddApiTool: () => void;
    onEditApiTool: (tool: APIToolConfig) => void;
    onDeleteApiTool: (toolId: string) => void;
}

function ToolsConfiguration({
    predefinedTools,
    apiTools,
    permission,
    onTogglePredefinedTool,
    onToggleApiTool,
    onAddApiTool,
    onEditApiTool,
    onDeleteApiTool
}: ToolsConfigurationProps) {
    return (
        <Accordion.Item eventKey="2" style={{ backgroundColor: '#2d2d2d', border: '1px solid #444', marginBottom: '1rem' }}>
            <Accordion.Header style={{ backgroundColor: '#2d2d2d' }}>
                <div className="d-flex align-items-center">
                    <div className="bg-warning rounded p-2 me-3">
                        <FaTools className="text-dark" />
                    </div>
                    <div>
                        <span className="text-light fw-bold">Tools & Integrations</span>
                        <br />
                        <small style={{ color: '#999' }}>Enable predefined tools and configure custom API integrations</small>
                    </div>
                </div>
            </Accordion.Header>
            <Accordion.Body style={{ backgroundColor: '#2d2d2d' }}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h6 className="text-light mb-0">Available Tools</h6>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={onAddApiTool}
                        disabled={permission}
                    >
                        <FaPlus className="me-1" />
                        Add API Tool
                    </Button>
                </div>

                {/* Predefined Tools */}
                <div className="mb-4">
                    <h6 className="text-light mb-3">Predefined Tools</h6>
                    {predefinedTools.map(tool => {
                        return (
                            <div 
                                key={tool.id}
                                className="d-flex align-items-center justify-content-between p-3 mb-2"
                                style={{ 
                                    backgroundColor: tool.enabled ? '#1a2e1a' : '#2d2d2d',
                                    border: `1px solid ${tool.enabled ? '#28a745' : '#555'}`,
                                    borderRadius: '8px'
                                }}
                            >
                                <div className="d-flex align-items-center">
                                    <div 
                                        className={`me-3 d-flex align-items-center justify-content-center rounded ${tool.enabled ? 'text-success' : 'text-light'}`}
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            backgroundColor: tool.enabled ? '#28a745' : '#6c757d',
                                            color: 'white',
                                            fontSize: '12px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {tool.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <h6 className={`mb-0 ${tool.enabled ? 'text-light' : 'text-light'}`}>
                                            {tool.name}
                                        </h6>
                                        <small className="text-light opacity-75">{tool.description}</small>
                                    </div>
                                </div>
                                <Form.Check
                                    type="switch"
                                    checked={tool.enabled}
                                    onChange={() => onTogglePredefinedTool(tool.id)}
                                    disabled={permission}
                                />
                            </div>
                        );
                    })}
                </div>

                {/* API Tools */}
                <div>
                    <h6 className="text-light mb-3">Custom API Tools</h6>
                    {apiTools.length === 0 ? (
                        <div 
                            className="text-center p-4"
                            style={{ 
                                backgroundColor: '#2d2d2d',
                                border: '1px dashed #555',
                                borderRadius: '8px'
                            }}
                        >
                            <FaCode className="text-light opacity-75 mb-2" size={30} />
                            <p className="text-light mb-0">No custom API tools configured</p>
                            <small className="text-light opacity-75">Click "Add API Tool" to create your first custom tool</small>
                        </div>
                    ) : (
                        apiTools.map(tool => (
                            <div 
                                key={tool.id}
                                className="d-flex align-items-center justify-content-between p-3 mb-2"
                                style={{ 
                                    backgroundColor: tool.enabled ? '#1a2e1a' : '#2d2d2d',
                                    border: `1px solid ${tool.enabled ? '#28a745' : '#555'}`,
                                    borderRadius: '8px'
                                }}
                            >
                                <div className="d-flex align-items-center">
                                    <FaExternalLinkAlt 
                                        className={`me-3 ${tool.enabled ? 'text-success' : 'text-info'}`} 
                                        size={16} 
                                    />
                                    <div>
                                        <h6 className={`mb-0 ${tool.enabled ? 'text-light' : 'text-light'}`}>
                                            {tool.name}
                                        </h6>
                                        <small className="text-light opacity-75">{tool.description}</small>
                                        <br />
                                        <Badge bg="secondary" className="me-2">{tool.method}</Badge>
                                        <small className="text-light opacity-75">{tool.url}</small>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center">
                                    <Form.Check
                                        type="switch"
                                        checked={tool.enabled || false}
                                        onChange={() => onToggleApiTool(tool.id!)}
                                        disabled={permission}
                                        className="me-3"
                                    />
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm" 
                                        className="me-2"
                                        onClick={() => onEditApiTool(tool)}
                                        disabled={permission}
                                    >
                                        <FaEdit />
                                    </Button>
                                    <Button 
                                        variant="outline-danger" 
                                        size="sm"
                                        onClick={() => onDeleteApiTool(tool.id!)}
                                        disabled={permission}
                                    >
                                        <FaTrash />
                                    </Button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </Accordion.Body>
        </Accordion.Item>
    );
}

export default ToolsConfiguration;